import type { ComponentProps } from "react";
import { BigHead, bigHeadOptions } from "extended-bigheads";

export type SpeakerBigHeadProps = ComponentProps<typeof BigHead>;

/** 与 DiceBear seed 类似的确定性哈希（同一字符串结果稳定）。 */
export function hashStringToUint(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash >>> 0;
}

function pickFrom<T>(id: string, salt: string, list: readonly T[]): T {
  const h = hashStringToUint(`${id}\0${salt}`);
  return list[h % list.length];
}

/**
 * 将说话人稳定 ID 映射为 Big Heads 全量 props（避免组件内部 Math.random / 缺省随机）。
 * 同一 ID 始终得到同一头像。
 */
export function speakerIdToBigHeadProps(speakerId: string): SpeakerBigHeadProps {
  const o = bigHeadOptions;
  return {
    showBackground: pickFrom(speakerId, "showBackground", o.showBackground),
    backgroundColor: pickFrom(speakerId, "backgroundColor", o.backgroundColor) as NonNullable<
      SpeakerBigHeadProps["backgroundColor"]
    >,
    backgroundShape: pickFrom(speakerId, "backgroundShape", o.backgroundShape) as NonNullable<
      SpeakerBigHeadProps["backgroundShape"]
    >,
    accessory: pickFrom(speakerId, "accessory", o.accessory) as NonNullable<
      SpeakerBigHeadProps["accessory"]
    >,
    body: pickFrom(speakerId, "body", o.body) as NonNullable<SpeakerBigHeadProps["body"]>,
    clothing: pickFrom(speakerId, "clothing", o.clothing) as NonNullable<
      SpeakerBigHeadProps["clothing"]
    >,
    clothingColor: pickFrom(speakerId, "clothingColor", o.clothingColor) as NonNullable<
      SpeakerBigHeadProps["clothingColor"]
    >,
    eyebrows: pickFrom(speakerId, "eyebrows", o.eyebrows) as NonNullable<
      SpeakerBigHeadProps["eyebrows"]
    >,
    eyes: pickFrom(speakerId, "eyes", o.eyes) as NonNullable<SpeakerBigHeadProps["eyes"]>,
    facialHair: pickFrom(speakerId, "facialHair", o.facialHair) as NonNullable<
      SpeakerBigHeadProps["facialHair"]
    >,
    facialHairColor: pickFrom(speakerId, "facialHairColor", o.facialHairColor) as NonNullable<
      SpeakerBigHeadProps["facialHairColor"]
    >,
    graphic: pickFrom(speakerId, "graphic", o.graphic) as NonNullable<SpeakerBigHeadProps["graphic"]>,
    hair: pickFrom(speakerId, "hair", o.hair) as NonNullable<SpeakerBigHeadProps["hair"]>,
    hairColor: pickFrom(speakerId, "hairColor", o.hairColor) as NonNullable<
      SpeakerBigHeadProps["hairColor"]
    >,
    hat: pickFrom(speakerId, "hat", o.hat) as NonNullable<SpeakerBigHeadProps["hat"]>,
    hatColor: pickFrom(speakerId, "hatColor", o.hatColor) as NonNullable<SpeakerBigHeadProps["hatColor"]>,
    lashes: pickFrom(speakerId, "lashes", o.lashes),
    lipColor: pickFrom(speakerId, "lipColor", o.lipColor) as NonNullable<
      SpeakerBigHeadProps["lipColor"]
    >,
    mouth: pickFrom(speakerId, "mouth", o.mouth) as NonNullable<SpeakerBigHeadProps["mouth"]>,
    skinTone: pickFrom(speakerId, "skinTone", o.skinTone) as NonNullable<
      SpeakerBigHeadProps["skinTone"]
    >,
    faceMask: pickFrom(speakerId, "faceMask", o.faceMask),
    faceMaskColor: pickFrom(speakerId, "faceMaskColor", o.faceMaskColor) as NonNullable<
      SpeakerBigHeadProps["faceMaskColor"]
    >,
  };
}
